import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCancelBooking } from '../flows/cancelBookingFlow';
import { CANCEL_MESSAGE_KEYS, cancelOutcomeMessage } from '../flows/cancelOutcome';
import { CancelSaveMode } from '../api/booking/bookingWriteApiSlice';
import { getApiErrorField } from '../utils/apiError';

type Severity = 'success' | 'info' | 'warning' | 'error';

/**
 * "Decline" on a room reservation awaiting approval (M8, #1809), for the venue
 * and apartment detail pages: the dialog's open state and what happens on
 * confirm. A decline is the cancel request with `decline` set, so it runs
 * through `useCancelBooking` — same refresh, same loading flag.
 *
 * `onOutcome` gets the message to show; `onDeclined` runs after a decline
 * that went through (the pages navigate back to the list, as after a cancel).
 */
export const useDeclineBooking = (
    onOutcome: (message: string, severity: Severity) => void,
    onDeclined: () => void,
) => {
    const { t } = useTranslation();
    const cancelBooking = useCancelBooking();
    const [isDeclineDialogOpen, setIsDeclineDialogOpen] = useState(false);

    const confirmDecline = async (saveMode: CancelSaveMode, reason: string | null) => {
        try {
            const response = await cancelBooking(saveMode, { reason });
            setIsDeclineDialogOpen(false);
            const outcome = cancelOutcomeMessage(saveMode, response.mailSent, CANCEL_MESSAGE_KEYS.decline);
            onOutcome(t(outcome.messageKey), outcome.severity);
            onDeclined();
        } catch (error) {
            setIsDeclineDialogOpen(false);
            // 409 `not_awaiting_approval`: someone approved (or cancelled) it
            // meanwhile. Said as such, rather than as a generic failure that
            // invites a retry which can never succeed.
            const raced = getApiErrorField(error, 'reason') === 'not_awaiting_approval';
            onOutcome(
                t(raced
                    ? 'booking_details.decline_booking.not_awaiting_message'
                    : 'booking_details.decline_booking.error_message'),
                'error',
            );
        }
    };

    return {
        isDeclineDialogOpen,
        openDeclineDialog: () => setIsDeclineDialogOpen(true),
        closeDeclineDialog: () => setIsDeclineDialogOpen(false),
        confirmDecline,
    };
};
